import { useEffect, useState, useCallback, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { getFirebaseMessaging, VAPID_KEY } from '@/lib/firebase/config';
import {
  pushNotificationsApi,
  WEB_PUSH_TOKEN_KEY,
  WEB_PUSH_TOKEN_OWNER_KEY,
} from '@/lib/api/services/pushNotifications';
import { useAuth } from '@/components/AuthProvider';

const STORAGE_KEY_TOKEN = WEB_PUSH_TOKEN_KEY;
const STORAGE_KEY_DENIED = 'fcm_permission_denied';
const STORAGE_KEY_DISABLED_PREFIX = 'fcm_disabled_by_user_';

export function isPushDisabledByUser(userId: string | undefined | null): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(STORAGE_KEY_DISABLED_PREFIX + userId) === 'true';
  } catch {
    return false;
  }
}

const PUSH_STATE_CHANGE_EVENT = 'fcm:state-changed';

function readMasterEnabled(): boolean {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return false;
  try {
    return (
      Notification.permission === 'granted' &&
      Boolean(localStorage.getItem(STORAGE_KEY_TOKEN))
    );
  } catch {
    return false;
  }
}

export function useIsPushMasterEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => readMasterEnabled());

  useEffect(() => {
    const refresh = () => setEnabled(readMasterEnabled());
    window.addEventListener(PUSH_STATE_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    refresh();
    return () => {
      window.removeEventListener(PUSH_STATE_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return enabled;
}

function dispatchPushStateChange() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(PUSH_STATE_CHANGE_EVENT));
  } catch {
  }
}

interface UsePushNotificationsOptions {
  autoRequest?: boolean;
  onForegroundMessage?: (payload: MessagePayload) => void;
}

interface UsePushNotificationsReturn {
  permission: NotificationPermission | 'unsupported';
  token: string | null;
  loading: boolean;
  requestPermission: (opts?: { silent?: boolean }) => Promise<boolean>;
  refreshPermission: () => Promise<NotificationPermission | 'unsupported'>;
  unregister: () => Promise<void>;
  isSupported: boolean;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {}
): UsePushNotificationsReturn {
  const { autoRequest = false, onForegroundMessage } = options;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY_TOKEN)
  );
  const [loading, setLoading] = useState(false);
  // Read by the long-lived onMessage listener, which must see the CURRENT user.
  const currentUserIdRef = useRef<string | null>(user?.id ?? null);
  currentUserIdRef.current = user?.id ?? null;

  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  const requestPermission = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    const silent = opts?.silent === true;
    if (!isSupported) {
      console.warn('[PushNotifications] Not supported in this browser');
      return false;
    }

    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        localStorage.setItem(STORAGE_KEY_DENIED, 'true');
        if (!silent) toast({
          title: 'Notifications blocked',
          description: 'You can enable them anytime in your browser settings.',
          variant: 'destructive',
        });
        return false;
      }

      localStorage.removeItem(STORAGE_KEY_DENIED);

      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.warn('[PushNotifications] Firebase Messaging not available');
        return false;
      }

      const fbSwRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
        updateViaCache: 'none',
      });

      if (fbSwRegistration.installing || fbSwRegistration.waiting) {
        await new Promise<void>((resolve) => {
          const worker = fbSwRegistration.installing || fbSwRegistration.waiting;
          if (!worker) return resolve();
          if (worker.state === 'activated') return resolve();
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') resolve();
          });
        });
      }

      const fcmToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: fbSwRegistration,
      });

      if (!fcmToken) {
        console.warn('[PushNotifications] Failed to get FCM token');
        return false;
      }

      await pushNotificationsApi.registerDevice(fcmToken, 'web');
      localStorage.setItem(STORAGE_KEY_TOKEN, fcmToken);
      if (user?.id) {
        localStorage.setItem(WEB_PUSH_TOKEN_OWNER_KEY, user.id);
        localStorage.removeItem(STORAGE_KEY_DISABLED_PREFIX + user.id);
      }
      setToken(fcmToken);
      dispatchPushStateChange();

      if (!silent) toast({ variant: "success",
        title: 'Notifications enabled',
        description: 'You will receive push notifications for important events.',
      });

      return true;
    } catch (error: any) {
      console.error('[PushNotifications] Error requesting permission:', error);
      if (!silent) toast({
        title: 'Failed to enable notifications',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, user?.id]);

  const refreshPermission = useCallback(async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!isSupported || typeof Notification === 'undefined') return 'unsupported';

    const current = Notification.permission;
    setPermission(current);

    if (current === 'granted') {
      localStorage.removeItem(STORAGE_KEY_DENIED);
      if (!localStorage.getItem(STORAGE_KEY_TOKEN)) {
        await requestPermission();
      }
    } else if (current === 'denied') {
      toast({
        title: 'Still blocked',
        description: 'Notifications are still blocked in your browser. Follow the steps above, then re-check.',
        variant: 'destructive',
      });
    } else {
      localStorage.removeItem(STORAGE_KEY_DENIED);
    }

    return current;
  }, [isSupported, requestPermission]);

  const unregister = useCallback(async (): Promise<void> => {
    const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!storedToken) return;

    try {
      await pushNotificationsApi.unregisterDevice(storedToken);
    } catch (error) {
      console.error('[PushNotifications] Failed to unregister:', error);
    } finally {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(WEB_PUSH_TOKEN_OWNER_KEY);
      if (user?.id) {
        localStorage.setItem(STORAGE_KEY_DISABLED_PREFIX + user.id, 'true');
      }
      setToken(null);
      dispatchPushStateChange();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isSupported || permission !== 'granted') return;

    let unsubscribe: (() => void) | undefined;

    (async () => {
      const messaging = await getFirebaseMessaging();
      if (!messaging) return;

      unsubscribe = onMessage(messaging, (payload) => {
        const data = payload.data || {};
        // This browser's token can still be bound to a previous user until it
        // is re-registered; never surface a push addressed to someone else.
        const recipient = data.recipient_user_id as string | undefined;
        if (recipient && recipient !== currentUserIdRef.current) return;
        const title = (data.title as string) || payload.notification?.title || 'WeNext';
        const body = (data.body as string) || payload.notification?.body || '';

        const type = (data.type || '').toUpperCase();
        let targetUrl: string | null = null;

        if (type === 'MESSAGE_RECEIVED' && data.conversationId) {
          targetUrl = `/home/inbox?conversationId=${data.conversationId}`;
        } else if (['CAMPAIGN_CREATED', 'CAMPAIGN_STARTED', 'CAMPAIGN_COMPLETED', 'CAMPAIGN_COMPLETED_WITH_ERRORS'].includes(type)) {
          targetUrl = '/home/campaigns';
        } else if (['CONTACT_IMPORT_COMPLETED', 'CONTACT_IMPORT_FAILED'].includes(type)) {
          targetUrl = '/home/contacts';
        } else if (type.startsWith('TEMPLATE_')) {
          targetUrl = '/home/templates';
        } else if (type === 'BUSINESS_ACCOUNT_STATUS_CHANGE') {
          targetUrl = '/home/integrations/whatsapp-business';
        } else if (type === 'TEAM_INVITATION') {
          targetUrl = '/home/team-management';
        } else if (type === 'TICKET_ASSIGNED' || type === 'TICKET_COMMENT') {
          targetUrl = '/home/tickets';
        } else if (data.url) {
          targetUrl = data.url;
        }

        const mediaType = data.media_type as string | undefined;
        const mediaUrl = data.media_url as string | undefined;
        const description =
          mediaType === 'image' && mediaUrl
            ? createElement(
                'div',
                { className: 'flex flex-col gap-2' },
                createElement('span', null, body),
                createElement('img', {
                  src: mediaUrl,
                  alt: '',
                  className: 'max-h-40 w-full rounded-md object-cover',
                })
              )
            : body;

        toast({
          title,
          description,
          duration: 6000,
          variant: 'info',
          action: targetUrl
            ? createElement(
                ToastAction,
                {
                  altText: 'View',
                  onClick: () => {
                    const current = window.location.pathname + window.location.search;
                    if (current !== targetUrl) {
                      navigate(targetUrl!);
                    }
                  },
                },
                'View'
              )
            : undefined,
        });

        if (onForegroundMessage) {
          onForegroundMessage(payload);
        }
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [isSupported, permission, onForegroundMessage]);

  useEffect(() => {
    if (!autoRequest || !isSupported) return;
    if (permission !== 'default') return;
    if (localStorage.getItem(STORAGE_KEY_DENIED) === 'true') return;

    const timer = setTimeout(() => {
      requestPermission();
    }, 2000);

    return () => clearTimeout(timer);
  }, [autoRequest, isSupported, permission, requestPermission]);

  useEffect(() => {
    if (!isSupported) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        const url = event.data.url as string;
        const current = window.location.pathname + window.location.search;
        if (current !== url) {
          navigate(url);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [isSupported, navigate]);

  return {
    permission,
    token,
    loading,
    requestPermission,
    refreshPermission,
    unregister,
    isSupported,
  };
}
